<template>
  <div
      class="cart-item-swipeable"
      @touchstart.passive="onTouchStart"
      @touchmove.passive="onTouchMove"
      @touchend="onTouchEnd"
      @mousedown="onMouseDown"
  >
    <!-- Right actions: delete (trash) - revealed when swiping row left -->
    <div v-if="!fromOrderPage && !readonly" class="swipe-actions swipe-actions--right">
      <button
          type="button"
          class="swipe-action swipe-action--delete"
          aria-label="Delete"
          @click="handleDelete"
      >
        <span class="iconfont-trash"></span>
      </button>
    </div>

    <!-- Main content row -->
    <div
        ref="contentRef"
        class="cart-item-content"
        :style="{ transform: `translateX(${translateX}px)` }"
    >
      <div class="cart-item">
        <!-- Whole row (image + description) is one link when productUrl exists and PDP is available -->
        <a
            v-if="productUrl && clickOn === 'all' && item.has_pdp"
            :href="isClickActionEnabled ? productUrl : ''"
            class="cart-item-link"
        >
          <div class="image">
            <img
                :src="getItemImage(item)"
                :alt="item.product?.title || item.product?.name"
            />
          </div>
          <div class="description-block">
            <button
                v-if="!fromOrderPage && !readonly"
                type="button"
                class="iconfont-cross cross-btn"
                @click.stop.prevent="handleDelete"
                :aria-label="$t('shopping.quickcart.remove')"
            />
            <div class="item-info">
              <div class="brand">{{ item.product?.brand?.name }}</div>
              <div class="name" :title="item.product?.name">{{ displayName }}</div>
              <span v-if="item.deal_type" class="beauty-deal-label">{{ item.deal_type }}</span>
              <div class="price-row">
              <span class="price" :class="{ 'sale-category' :item.product.is_sale }"
              >{{ item.total || item.product?.price }}</span>
                <span v-if="item.product?.size" class="volume">{{ item.product.size }}</span>
                <!-- Qty inside link but @click.stop prevents navigation -->
                <div v-if="item.allow_quantity !== false" class="quantity-row">
                  <button type="button" class="qty-tap" :class="readonly ? 'readonly' : ''" @click.stop.prevent="handleQtyClick">
                    {{ $t('my_account.orders.qty') }}:{{ item.quantity }}
                    <span v-if="!readonly" class="qty-arrow iconfont-triangle-right"></span>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </a>
        <template v-else>
          <div class="image" @click="isClickActionEnabled ? handleImageClick : null">
            <img
                :src="getItemImage(item)"
                :alt="item.product?.title || item.product?.name"
            />
          </div>
          <div class="description-block">
            <div class="description" @click="isClickActionEnabled ? handleRowClick : null">
              <div class="item-info">
                <div class="brand">{{ item.product?.brand?.name }}</div>
                <div class="name" :title="item.product?.name">{{ displayName }}</div>
                <span v-if="item.deal_type" class="beauty-deal-label">{{ item.deal_type }}</span>
                <div class="price-row">
                <span class="price" :class="{ 'sale-category' :item.product.is_sale }"
                >{{ item.total || item.product?.price }}</span>
                  <span v-if="item.product?.size" class="volume">{{ item.product.size }}</span>
                  <div v-if="item.allow_quantity !== false" class="quantity-row">
                    <button type="button" class="qty-tap" :class="readonly ? 'readonly' : ''" @click.stop="handleQtyClick">
                      {{ $t('my_account.orders.qty') }}:{{ item.quantity }}
                      <span v-if="!readonly" class="qty-arrow iconfont-triangle-right"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>

</template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { getItemImage } from '@/helper'

const props = defineProps({
  freezed: { type: Boolean, default: false },
  item: { type: Object, required: true },
  /** Pre-computed product URL for navigation. When set, row/image use <a> for reliable clicks. */
  productUrl: { type: String, default: null },
  /** Called when Qty is clicked - opens edit quantity bottomsheet */
  onEditQuantity: { type: Function, default: null },
  /** When true, hide delete swipe (e.g. order confirmation page). */
  fromOrderPage: { type: Boolean, default: false },
  /** When true (e.g. checkout context), hide QTY button and disable swipe. */
  readonly: { type: Boolean, default: false },
  /** 'all' = row and image navigate; 'image' = only image navigates. */
  clickOn: { type: String, default: 'all' },
  isClickActionEnabled: { type: Boolean, default: false },
})

const emit = defineEmits(['delete', 'edit-quantity', 'press-product'])

const contentRef = ref(null)
const translateX = ref(0)
const touchStartX = ref(0)
const touchStartY = ref(0)

const SWIPE_THRESHOLD = 60
const DELETE_ACTION_WIDTH = 74

const displayName = computed(() => {
  const name = props.item.product?.name || props.item.product?.title || ''
  return name.length > 45 ? `${name.slice(0, 42)}...` : name
})

function closeSwipeable() {
  translateX.value = 0
}

function handleDelete() {
  closeSwipeable()
  emit('delete', props.item)
}

function handleQtyClick(e) {
  if (props.readonly || props.freezed) return

  e.stopPropagation()
  e.preventDefault()
  closeSwipeable()
  if (props.onEditQuantity) {
    props.onEditQuantity(props.item)
  } else {
    emit('edit-quantity', props.item)
  }
}

function handleRowClick() {
  if (props.freezed || didDrag.value || !props.item.has_pdp) return
  closeSwipeable()
  if (props.clickOn === 'all') {
    emit('press-product', props.item)
  }
}

function handleImageClick(e) {
  e.stopPropagation()
  if (props.freezed || didDrag.value || !props.item.has_pdp) return
  closeSwipeable()
  if (props.clickOn === 'all' || props.clickOn === 'image') {
    emit('press-product', props.item)
  }
}


function onTouchStart(e) {
  if (props.freezed) return

  touchStartX.value = e.touches[0].clientX
  touchStartY.value = e.touches[0].clientY
}

const swipeDisabled = computed(() => props.fromOrderPage || props.readonly)

function onTouchMove(e) {
  if (swipeDisabled.value) return
  const dx = e.touches[0].clientX - touchStartX.value
  const dy = e.touches[0].clientY - touchStartY.value
  if (Math.abs(dx) > Math.abs(dy)) {
    translateX.value = Math.max(-DELETE_ACTION_WIDTH, Math.min(dx, 0))
  }
}

function onTouchEnd() {
  if (swipeDisabled.value) return
  if (translateX.value <= -SWIPE_THRESHOLD) {
    translateX.value = -DELETE_ACTION_WIDTH
  } else {
    translateX.value = 0
  }
  touchStartX.value = 0
  touchStartY.value = 0
}

// Mouse drag support for desktop (simulates swipe)
const isDragging = ref(false)
const didDrag = ref(false)

function onMouseDown(e) {
  if (swipeDisabled.value) return
  if (e.button !== 0) return
  if (e.target.closest('button, a')) return

  isDragging.value = true
  didDrag.value = false
  touchStartX.value = e.clientX
  touchStartY.value = e.clientY
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

function onMouseMove(e) {
  if (props.freezed || !isDragging.value) return

  const dx = e.clientX - touchStartX.value
  const dy = e.clientY - touchStartY.value

  if (Math.abs(dx) > Math.abs(dy)) {
    didDrag.value = true
    translateX.value = Math.max(-DELETE_ACTION_WIDTH, Math.min(dx, 0))
  }
}

function onMouseUp() {
  if (props.freezed || !isDragging.value) return

  isDragging.value = false
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
  if (translateX.value <= -SWIPE_THRESHOLD) {
    translateX.value = -DELETE_ACTION_WIDTH
  } else {
    translateX.value = 0
  }
  touchStartX.value = 0
  touchStartY.value = 0
  setTimeout(() => { didDrag.value = false }, 0)
}

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})

defineExpose({ closeSwipeable })
</script>
